import React, { FC, PropsWithChildren, useCallback, useEffect, useId, useRef } from 'react'

import clsx from 'clsx'
import Modal from '@mui/material/Modal'
import Fade from '@mui/material/Fade'
import { useTranslation } from 'react-i18next'
import { CloseIcon } from '@svgs'

export interface IMyModal {
  open: boolean
  handleClose: () => void
  width?: string
  className?: string
  contentClassName?: string
  overflowAuto?: boolean
  // Every modal gets a close button in its top corner; modals that draw their
  // own (on a photo, in a header) turn this one off.
  showClose?: boolean
}

// The one modal of the site: a dialog named by its first heading (the old one
// pointed aria-labelledby at an id no modal had, and had no dialog role, so a
// screen reader announced nothing), radius 16, the system's pop-up shadow.
const MyModal: FC<PropsWithChildren<IMyModal>> = ({
  open,
  handleClose,
  width = 'md:w-[81.5rem] w-[calc(100%_-_2rem)]',
  children,
  className,
  contentClassName,
  overflowAuto = true,
  showClose = true,
}) => {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  const nameByHeading = useCallback(
    (dialog: HTMLElement | null) => {
      const heading = dialog?.querySelector('h1, h2, h3')
      if (!dialog || !heading) return
      if (!heading.id) heading.id = titleId
      dialog.setAttribute('aria-labelledby', heading.id)
    },
    [titleId],
  )
  // the portal mounts the dialog a render later than this component commits:
  // name it when the fade starts, and again if its content changes
  useEffect(() => {
    if (open) nameByHeading(dialogRef.current)
  }, [open, nameByHeading, children])

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open} onEnter={(node) => nameByHeading(node)}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          className={clsx(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface text-ink rounded-2xl shadow-pop',
            width,
            className,
          )}
        >
          {showClose && (
            <button
              type="button"
              onClick={() => handleClose()}
              aria-label={t('close')}
              className="absolute z-20 top-1.5 right-1.5 w-11 h-11 flex items-center justify-center rounded-xl fill-ink-2 hover:fill-danger hover:bg-page focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
            >
              <span className="icon-button w-3.5 h-3.5">
                <CloseIcon />
              </span>
            </button>
          )}
          <div
            className={clsx(
              'max-h-[80vh] supports-[height:100dvh]:max-h-[85dvh] p-4',
              overflowAuto && 'overflow-y-auto',
              contentClassName,
            )}
          >
            {children}
          </div>
        </div>
      </Fade>
    </Modal>
  )
}

export default MyModal
